import { useCallback } from "react";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useRemoveOrgIntegrationAccountMutation } from "@api/organizationIntegrationAccount.api";
import { integrationAccountLabel } from "../integrationAccountLabel";
import { useIntegrationRowActions } from "./IntegrationRowActionsContext";

interface RemoveIntegrationAccountModalProps {
    item: OrganizationIntegrationAccountItem;
    show: boolean;
    onClose: () => void;
}

/**
 * Mirrors the workspace-level removal dialog in
 * `DisplayPlaidConnection.tsx` — same title, structure and Cancel/Confirm
 * labels, so removing from the firm table reads the same as removing from a
 * workspace.
 *
 * One deliberate difference: that dialog removes a whole connection and so says
 * "hide all <bank> accounts", while a row here is a single account. Naming the
 * account keeps the promise accurate — the other nine accounts under the same
 * login are untouched.
 */
export const RemoveIntegrationAccountModal = ({
    item,
    show,
    onClose,
}: RemoveIntegrationAccountModalProps) => {
    const { organizationId, onAccountRemoved } = useIntegrationRowActions();
    const removeAccount =
        useRemoveOrgIntegrationAccountMutation(organizationId);

    /**
     * A failure leaves the dialog open so the user can retry or back out — the
     * interceptor has already toasted the reason, and a 409 (manual journal
     * entries) needs them to go and clear those first.
     *
     * Swallowed rather than rethrown: `ButtonWithLoader` resets its spinner in a
     * `.finally()` with no `.catch()`, so a rejection escaping here would become
     * an unhandled promise rejection.
     */
    const handleConfirm = useCallback(async () => {
        try {
            await removeAccount.mutateAsync(item.account.id);
        } catch {
            return;
        }
        onClose();
        onAccountRemoved();
    }, [removeAccount, item.account.id, onClose, onAccountRemoved]);

    return (
        <ConfirmationModal
            show={show}
            onHide={onClose}
            title="Remove from Kick?"
            question={
                <>
                    This action will hide{" "}
                    <strong>{integrationAccountLabel(item)}</strong> from Kick
                    and prevent Kick from searching for tax deductions or cash
                    back savings.
                    <br />
                    <br />
                    Do you wish to continue?
                </>
            }
            onConfirm={handleConfirm}
            onReject={onClose}
            yes="Confirm"
            no="Cancel"
        />
    );
};
